// shared/parse-text.ts — the ONE client-side port of PHP parse_text() (includes/functions.php:4093).
//
// Quill-authored rich text → HTML. Consolidates the two drifted copies that used to live in
// includes/js-src/log/ and includes/js-src/player-character-sheet/, plus the deferred port in
// object-card/. Imported by the log, player-character-sheet, object-card and parse-text bundles.
//
// PARITY: mirrors the PHP handler ORDER exactly — nl2br → <table> class injection → ordered tag
// handlers → clean_quill_content → wrap in <span class='textarea-output'>. The two DB-backed tags
// ([table:id] / [random_table:id]) cannot run synchronously client-side, so parseText() emits a
// lazy-mount placeholder that ftMountParseTextTables() hydrates via the on-demand endpoints
// (options/api/parse_text_table_inc.php / parse_text_random_table_inc.php).
//
// DIVERGENCE RESOLUTION (which old copy was right):
//   nl2br order .......... PC-sheet: '<br />' BEFORE the newline (PHP nl2br() default).
//   cleanQuillContent .... PC-sheet: present (the log copy was MISSING it).
//   richLink icon args ... PC-sheet: iconMap(id,20,20,true)  (PHP icon_ph($id,20,20,true)).
//   damage rollType ...... log: ROLLTYPE_CATALOG.damage_roll.item_id || 'damage_roll'.
//   handler order ........ log: explicit ordered patterns[] array.
//   value escaping ....... log: _esc() on interpolated roll/uuid values. PHP does NOT escape these,
//                          but for valid data (digits / +N / UUID) it's a no-op and it hardens a
//                          dirty blob. Revert to raw interpolation if strict byte-parity is needed.
//   damage name→UUID ..... NEW: both copies failed [damage_roll:1d6|fire]; resolve via
//                          DAMAGETYPE_CATALOG before the UUID check (PHP DamageType::getId()).
//
// XSS: parse_text is a FORMATTER, not a sanitizer (both sides). The whole input is NOT escaped —
// Quill HTML passes through; only specific handler-captured values are escaped. Trust boundary is
// Quill authoring + upstream per-viewer redaction.

// ── global accessors ────────────────────────────────────────────────────────────────────────────
// These are page-globals from core-helpers.js + the boot island (iconMap / escapeHtml /
// ucwordsAndSpace / checkIfValidUUID / BASE_URL / ROLLTYPE_CATALOG / DAMAGETYPE_CATALOG). Read them
// through globalThis so this shared module needs NO ambient .d.ts and compiles cleanly in every
// bundle project (each bundle declares its own globals; redeclaring here would conflict).
import { renderTableColumns, renderRandomTable } from './render-table-tag';
import { resolveRandomTableTagInputs } from './random-table-tag-inputs';

const _g: any = globalThis as any;

function escapeHtmlSafe(s: unknown): string {
    if (typeof _g.escapeHtml === 'function') return _g.escapeHtml(String(s ?? ''));
    const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s ?? '').replace(/[&<>"']/g, m => map[m] ?? m);
}

function iconHtml(key: string, w?: number, h?: number, popup?: boolean): string {
    if (typeof _g.iconMap !== 'function') return '';
    if (w == null && h == null) return _g.iconMap(key);
    // 4th arg: PHP icon_ph $show_popup_text. iconMap()'s 4th arg is the title/false flag — pass the
    // key as title when popup, false for a bare img (mirrors icon_ph(...,true) wrapping behaviour).
    return _g.iconMap(key, w || 20, h || w || 20, popup ? '' : false);
}

function isUuid(s: string): boolean {
    if (typeof _g.checkIfValidUUID === 'function') return _g.checkIfValidUUID(s);
    return /^\{?[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}\}?$/.test(s);
}

function ucwords(s: string): string {
    if (typeof _g.ucwordsAndSpace === 'function') return _g.ucwordsAndSpace(s);
    return s.replace(/\b\w/g, c => c.toUpperCase());
}

function baseUrl(): string {
    return (typeof _g.BASE_URL !== 'undefined' && _g.BASE_URL) ? String(_g.BASE_URL) : '/';
}

// PHP DamageType::getId($input) ?? $input — accepts a variable_name or a UUID, returns the UUID.
function resolveDamageTypeId(input: string): string {
    const cat = _g.DAMAGETYPE_CATALOG;
    if (cat && typeof cat === 'object') {
        const hit = cat[input] || cat[input.toLowerCase()];
        if (hit && hit.item_id) return String(hit.item_id);
    }
    return input;
}

// PHP RollType::getId('damage_roll') ?? 'damage_roll'.
function damageRollTypeId(): string {
    const cat = _g.ROLLTYPE_CATALOG;
    if (cat && cat.damage_roll && cat.damage_roll.item_id) return String(cat.damage_roll.item_id);
    return 'damage_roll';
}

// ── string primitives (mirror PHP) ────────────────────────────────────────────────────────────────
// PHP nl2br() default inserts "<br />" BEFORE the line break and keeps the newline.
function nl2br(s: string): string {
    return s.replace(/(\r\n|\n\r|\r|\n)/g, '<br />$1');
}

function injectTableClass(s: string): string {
    return s.replace(/<table>/g, "<table class='table table-bordered table-striped table-hover mb-2'>");
}

// clean_quill_content() — drop empty <p>, collapse runs of <br>, strip trailing <br>, trim.
function cleanQuillContent(content: string): string {
    let out = content.replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '');
    out = out.replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br>');
    out = out.replace(/(?:<br\s*\/?>\s*)+$/gi, '');
    return out.trim();
}

// ── tag handlers ──────────────────────────────────────────────────────────────────────────────────

// [roll:XdY+mod] → raw-dice roll button (rollDice is an ambient template.js global at click time).
function handleRoll(_m: string, inner: string): string {
    const firstChar = inner.slice(0, 1);
    let dice: string | number;
    let die: string | number;
    let mod: string | number;
    if (!/[0-9]/.test(firstChar)) {
        dice = 1;
        die = inner.slice(1);
        mod = 0;
    } else {
        const p = /(\d+)d(\d+)([+-]\d+)?/.exec(inner);
        dice = p?.[1] ?? 1;
        die = p?.[2] ?? 20;
        mod = (p && p[3]) ? p[3] : 0;
    }
    const display = Number(mod) === 0 ? `${dice}d${die}` : `${dice}d${die}${mod}`;
    return `<button class='btn btn-sm btn-danger' onclick="rollDice('raw', '', '', '', '${escapeHtmlSafe(dice)}', '${escapeHtmlSafe(die)}', '${escapeHtmlSafe(mod)}', '');">${escapeHtmlSafe(display)}</button>`;
}

// [damage_roll:XdY+mod|damage_type_name_or_UUID] → damage-roll button with the damage-type icon.
function handleDamageRoll(_m: string, rollStr: string, damageInput: string): string {
    const p = /(\d+)d(\d+)([+-]\d+)?/.exec(rollStr);
    const dice = p?.[1] ?? 1;
    const die = p?.[2] ?? 10;
    const mod = (p && p[3]) ? p[3] : 0;
    const displayMod = Number(mod) !== 0 ? (Number(mod) > 0 ? `+${Number(mod)}` : String(mod)) : '';
    const displayRoll = `${dice}d${die}${displayMod}`;

    const damageUuid = resolveDamageTypeId(String(damageInput || '').trim());
    if (!isUuid(damageUuid)) {
        return `[Unknown damage type: ${escapeHtmlSafe(damageInput)}]`;
    }
    return `<button class='btn btn-sm btn-danger' onclick="rollDice('${escapeHtmlSafe(damageRollTypeId())}', '${escapeHtmlSafe(damageUuid)}', '', '', '${escapeHtmlSafe(dice)}', '${escapeHtmlSafe(die)}', '${escapeHtmlSafe(mod)}', '0');">${escapeHtmlSafe(displayRoll)} ${iconHtml(damageUuid, 18, 18, true)}</button>`;
}

// [table:id] / [random_table:id] → DB-backed; emit a lazy-mount placeholder hydrated by
// ftMountParseTextTables() via the on-demand endpoints. id-only payload → a data- attr suffices.
function tableMount(kind: 'table' | 'random_table', id: string): string {
    return `<span class="ft-pt-table-mount" data-pt-kind="${kind}" data-pt-id="${escapeHtmlSafe(id)}" data-pt-format="default"></span>`;
}

// [icon_map:id] / [icon:id] and [N.Nap] / [rap] → icon.
function handleIcon(_m: string, inner: string): string {
    return iconHtml(inner);
}

// [blocktext]...[/blocktext]
function handleBlocktext(_m: string, inner: string): string {
    return `<div class='ft-parsed-blocktext'>${inner}</div>`;
}

// "At Higher Levels." → section divider + badge.
function handleAtHigherLevels(): string {
    return "<div class='mb-3 border-top'></div><span class='badge bg-success'>At Higher Levels</span>";
}

// True when iconMap() rendered the help-circle-outline fallback (couldn't resolve the key). Inspect the
// real <img src="…">, NOT a bare substring — every iconMap() output also carries help-circle-outline in
// its onerror="this.src='…help-circle-outline.png'" handler, so a substring check is ALWAYS true. No
// <img> at all → a rune TYPE-BADGE (<svg> span) or its deferred ft-icon placeholder = a RESOLVED icon.
// (Mirror of list-display/helpers.ts _isUnknownIcon.)
function isUnknownIconHtml(ico: string): boolean {
    if (typeof ico !== 'string' || ico === '') return true;
    const m = ico.match(/\ssrc="([^"]*)"/);
    if (!m) return false;
    return m[1]?.indexOf('help-circle-outline') !== -1;
}

// Resolve the leading icon for a [Name](type:id) rich link with a graceful fallback chain so game
// "terms" ([ADV](term:uuid), [Reaction](term:uuid), [Ability](term:uuid)) render a meaningful icon
// instead of the help-circle "?". The authored UUID is bespoke catalog art NOT in the client icon
// registry, so help-circle is all iconMap(uuid) can give. Chain (per the desired richer behaviour):
//   1) the id (UUID)   — keeps registered UUIDs working (e.g. [Healing Roll](roll_type:uuid)),
//   2) the name slug   — the registry IS keyed by name ("ADV"→adv, "Reaction"→reaction, "Ability"→ability),
//   3) the link type   — a typed-but-unresolved link still shows its category icon (term/roll_type) over "?".
// Returns the id-based result (the help-circle) as a last resort if nothing resolves.
// Entity / instance / reference link types → their CATEGORY icon key. For these, the authored id is
// a row UUID (never registry art) and the label is prose, so the "what kind of thing is this" glyph
// is exactly right — a creature link shows the creature icon, an encounter (other-room) link the
// encounter icon, etc. (there is no dedicated `encounter_creature` icon, so it borrows `creature`).
// This makes copy-safe instance tags (encounter_creature:/inventory:/encounter:/location:) read at a
// glance instead of falling through to the help-circle "?".
const LINK_TYPE_ICON: Record<string, string> = {
    creature: 'creature',
    encounter_creature: 'creature',
    inventory: 'inventory',
    encounter: 'encounter',
    location: 'location',
};

// ⭐ Article SUBTYPE icons (2026-08-23, owner). An article's identity is its `article_type` — a
// catalog row (items.item_type='article_type': Deity, Organization, Settlement, …) — so a link to
// Aspis should show the DEITY mark, not a generic document. The tag carries it as a dotted subtype:
//
//     [Aspis](article.deity:<uuid>)   → deity icon, article.php URL
//     [Aspis](article:<uuid>)         → still valid; falls back to the generic article icon
//
// ⚠ Aliases only where the type slug is not itself an icon name. Reuse existing art rather than
// minting — the same discipline the timeline medallion taxonomy used (it minted zero icons).
// Resolution order for a subtype: this map → the bare slug as an icon name → generic article.
const ARTICLE_SUBTYPE_ICON: Record<string, string> = {
    deity: 'skill_godlike',              // the radiant star `divine`/`celestial` already use
    character: 'creature',
    creature: 'legendary_creature',
    organization: 'guilds_and_factions',
    military_conflict: 'war',
    military_formation: 'guilds_and_factions',
    geographic_location: 'location',
    settlement: 'location',
    landmark: 'location',
    building: 'construction',
    item: 'inventory',
    document: 'book',
    prose: 'book',
    poem: 'book',
    myth: 'book',
    report: 'book',
    law: 'government',
    plot: 'lore',
    generic_article: 'lore',
};

function resolveRichLinkIcon(idRaw: string, name: string, type: string, subtype: string = ''): string {
    // ⭐ A subtype wins outright — it is the most specific thing the tag knows. An article's type
    // (Deity, Settlement, Organisation…) describes it far better than the word "article" does.
    if (subtype) {
        const aliased = ARTICLE_SUBTYPE_ICON[subtype] || subtype;
        const bySubtype = iconHtml(aliased, 20, 20, true);
        if (!isUnknownIconHtml(bySubtype)) return bySubtype;
    }

    // Entity/reference types resolve to their category icon up front (id + label never resolve for these).
    const typeIconKey = LINK_TYPE_ICON[type];
    if (typeIconKey) {
        const byTypeIcon = iconHtml(typeIconKey, 20, 20, true);
        if (!isUnknownIconHtml(byTypeIcon)) return byTypeIcon;
    }

    const byId = iconHtml(idRaw, 20, 20, true);
    if (!isUnknownIconHtml(byId)) return byId;

    const slug = String(name || '').trim().toLowerCase().replace(/\s+/g, '_');
    if (slug) {
        // Ability-type terms ("Reaction", "Free", "Passive", …) render the unified rune SHAPE badge,
        // whose canon keys are the *_ability forms (reaction_ability, …) — except the generic "ability"
        // which is itself a canon key. Promote the slug to its badge key (single source of truth:
        // ftAbilityBadgeType in core-helpers.js) so the term shows the shape, not a flat png. Try the
        // bare slug first ("ability"/"curse"/"menta" resolve directly), then the "<slug>_ability" form.
        const badgeOf = typeof _g.ftAbilityBadgeType === 'function' ? _g.ftAbilityBadgeType : null;
        const nameKey = (badgeOf && (badgeOf(slug) || badgeOf(slug + '_ability'))) || slug;
        const byName = iconHtml(nameKey, 20, 20, true);
        if (!isUnknownIconHtml(byName)) return byName;
    }
    if (type) {
        const byType = iconHtml(type, 20, 20, true);
        if (!isUnknownIconHtml(byType)) return byType;
    }
    return byId;
}

// [Display Name](type:id) → Quill-style rich link (icon + item/location link).
function handleQuillLink(_m: string, name: string, typeRaw: string, idRaw: string): string {
    const safeName = escapeHtmlSafe(name);
    // A dotted type carries a SUBTYPE: "article.deity" → kind 'article' (the URL) + 'deity' (the
    // icon). Splitting on the FIRST dot only, so a subtype containing one survives intact.
    const rawLower = String(typeRaw || '').toLowerCase();
    const dot = rawLower.indexOf('.');
    const type = dot === -1 ? rawLower : rawLower.slice(0, dot);
    const subtype = dot === -1 ? '' : rawLower.slice(dot + 1);
    const safeId = escapeHtmlSafe(idRaw);
    const icon = resolveRichLinkIcon(idRaw, name, type, subtype);
    // ⛔ Three kinds, not two. This was a location/item ternary until 2026-08-23, so
    // [Name](article:<uuid>) silently resolved to item.php?id=<article-uuid> — a dead link, because
    // an article is NOT an items row. Same non-exhaustive-pair bug the kin renderers carried
    // (kind === 'item' ? item.php : encounter.php), which is the third place a two-branch
    // conditional written before `article` existed has produced a broken href. If a fourth linkable
    // kind ever lands, extend this map — do not add another ternary.
    // article.php?id=<uuid> is a permanent address: it accepts the id form forever, so the parser
    // never needs the readable slug it does not have (ft_article_url() in ft_article_inc.php).
    const url = type === 'article'
        ? `${baseUrl()}article.php?id=${safeId}`
        : type === 'location'
            ? `${baseUrl()}location.php?location_id=${safeId}`
            : `${baseUrl()}item.php?id=${safeId}`;
    const typeLabel = ucwords(String(typeRaw || '').replace(/_/g, ' '));
    return `${icon} <a href='${url}' class='link-primary' title='${escapeHtmlSafe(typeLabel)}' target='_blank' rel='noopener noreferrer'>${safeName}</a>`;
}

type Replacer = (match: string, ...groups: any[]) => string;

/**
 * Client-side parse_text(). Returns the `.textarea-output`-wrapped HTML, or '' for empty input —
 * same contract as the PHP twin. [table:]/[random_table:] expand to lazy mounts (hydrate later).
 */
export function parseText(text: string | null | undefined): string {
    if (text === null || text === undefined || text === '') return '';

    let out = injectTableClass(nl2br(String(text)));

    // Order matches PHP $tagHandlers. The Quill rich-link pattern runs LAST so the other [tag:value]
    // patterns are consumed first and don't compete on bracket parsing.
    const patterns: Array<[RegExp, Replacer]> = [
        // Scene-tooling ownership markers ([cp-ft3d:<slug>…], written by the FT3D promotion probes into GM notes) are
        // machine identity, never prose: they render as nothing, and the paragraph they sat in is then emptied away.
        [/\[cp-ft3d:[^\]\s]*\]/g, () => ''],
        [/\[roll:([^\]]+)\]/g, handleRoll],
        [/\[damage_roll:([^|]+)\|([^\]]+)\]/g, handleDamageRoll],
        [/\[random_table:([^\]]+)\]/g, (_m, id: string) => tableMount('random_table', id)],
        [/\[(\d*\.?\d*ap|rap)\]/g, handleIcon],
        [/\[blocktext\]([\s\S]*?)\[\/blocktext\]/g, handleBlocktext],
        [/At Higher Levels\./gi, handleAtHigherLevels],
        [/\[table:([^\]]+)\]/g, (_m, id: string) => tableMount('table', id)],
        [/\[icon_map:([^\]]+)\]/g, handleIcon],
        [/\[icon:([^\]]+)\]/g, handleIcon],
        [/\[([^\]]+)\]\(([^:]+):([^)]+)\)/g, handleQuillLink],
    ];

    for (const [pattern, handler] of patterns) {
        out = out.replace(pattern, handler);
    }

    out = cleanQuillContent(out);
    return `<span class='textarea-output'>${out}</span>`;
}

// ── lazy hydration of [table:]/[random_table:] mounts ──────────────────────────────────────────────
// The endpoints return DATA ({kind, columns/rows | random-table rows}); render-table-tag.ts builds the
// <table> HTML client-side, running parseText() per cell — the front-end port of the old PHP
// get_table_columns()/get_random_table() builders. Per-page cache keyed `${kind}:${id}` so each table is
// fetched once across many mounts/repaints. Cell content may itself contain [table:]/[random_table:], so
// we recurse after injecting — capped by MAX_TABLE_DEPTH (mirrors the PHP fetch_table_column_rows
// $visited_ids cycle guard).
const MAX_TABLE_DEPTH = 3;
const tableCache = new Map<string, Promise<any>>();

function fetchTableData(kind: string, id: string): Promise<any> {
    const key = `${kind}:${id}`;
    let p = tableCache.get(key);
    if (!p) {
        const file = kind === 'random_table' ? 'parse_text_random_table_inc.php' : 'parse_text_table_inc.php';
        p = fetch(`${baseUrl()}options/api/${file}?id=${encodeURIComponent(id)}`)
            .then(r => r.json())
            .then(data => data?.kind === 'random_table' && data.definition_inputs ? resolveRandomTableTagInputs(data) : data)
            .catch(error => {
                tableCache.delete(key); // A transient read must not poison later AJAX mounts/refreshes.
                console.warn('ftMountParseTextTables: hydrate failed', error);
                return null;
            });
        tableCache.set(key, p);
    }
    return p;
}

/**
 * Hydrate every .ft-pt-table-mount placeholder under `root` (default document): fetch the endpoint data,
 * render the table via render-table-tag.ts, replace the placeholder, then re-run the icon/rune scanners
 * and recurse (depth-capped) for any nested [table:]/[random_table:] a cell produced. Empty/failed → drop
 * the placeholder (same as a literal-tag miss).
 */
export function ftMountParseTextTables(root?: Element | Document, depth = 0): void {
    if (depth > MAX_TABLE_DEPTH) return;
    const scope: Element | Document = root || document;
    const mounts = scope.querySelectorAll('.ft-pt-table-mount:not([data-pt-done])');
    for (let i = 0; i < mounts.length; i++) {
        const el = mounts[i] as HTMLElement;
        el.setAttribute('data-pt-done', '1'); // mark first so a re-scan never double-fetches
        const kind = el.getAttribute('data-pt-kind') || 'table';
        const id = el.getAttribute('data-pt-id') || '';
        const format = el.getAttribute('data-pt-format') || 'default';
        if (!id) { el.remove(); continue; }
        fetchTableData(kind, id).then(data => {
            const parent = el.parentNode as (Element | Document) | null;
            if (!parent) return;
            let html = '';
            if (data && data.kind === 'random_table') html = renderRandomTable({ ...data, format }, parseText);
            else if (data && data.kind === 'table') html = renderTableColumns(data, parseText);
            else if (data && typeof data.html === 'string') html = data.html; // back-compat
            if (!html) { el.remove(); return; } // empty → behave like a literal-tag miss
            const tpl = document.createElement('template');
            tpl.innerHTML = html;
            parent.replaceChild(tpl.content, el);
            if (typeof _g.ftMountIcons === 'function') _g.ftMountIcons(parent);
            if (typeof _g.ftMountRuneIcons === 'function') _g.ftMountRuneIcons(parent);
            ftMountParseTextTables(parent as Element | Document, depth + 1); // nested [table:] in cells
        }).catch(e => { console.warn('ftMountParseTextTables: hydrate failed', e); });
    }
}
